import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useProviderRFIMetrics,
  useProviderAssessmentMetrics,
  useGetVendorRfis,
  useSponsorRFIMetrics,
  useAssessmentMetrics,
  useGetSponsorCount,
} from "./queries";
import { findRfiColorMeta } from "../utils/serviceCategoriesColors";

const DEFAULT_TEAM_SIZE = 0;

// Request statuses that still count as open, per AB#14666.
// Same field grouping the status dashboards already render as "open".
const OPEN_VQQ_FIELDS = [
  "sent_count",
  "in_progress_count",
  "review_count",
  "remediation_count_purchaser",
  "remediation_count_vendor",
];

const OPEN_ASSESSMENT_FIELDS = [
  "total_requested_vqa_requests",
  "total_inprogress_vqa_requests",
];

// Shown instead of a number when the data behind it is incomplete.
const INCOMPLETE_VALUE = "–";

function readField(source, field, missingFields) {
  const value = source?.[field];
  if (typeof value !== "number") {
    missingFields.push(field);
    return 0;
  }
  return value;
}

function sumFields(source, fields, missingFields) {
  return fields.reduce(
    (total, field) => total + readField(source, field, missingFields),
    0,
  );
}

/**
 * Derives the request stats from one pair of metrics responses.
 * Total is the sum of the same two values the breakdown shows, so
 * "VQQ + Assessment = Total" holds by construction rather than by luck.
 * A field that is absent is recorded in missingFields instead of being
 * silently treated as zero.
 *
 * @param {Object} rfiMetrics - VQQ metrics response.
 * @param {Object} assessmentMetrics - Assessment metrics response.
 * @returns {{ vqqRequests, assessmentRequests, totalRequests, activeRequests, missingFields }}
 */
export function computeRequestStats(rfiMetrics, assessmentMetrics) {
  const missingFields = [];

  const vqqRequests = readField(rfiMetrics, "total_count", missingFields);
  const assessmentRequests = readField(
    assessmentMetrics,
    "total_vqa_requests",
    missingFields,
  );

  const activeRequests =
    sumFields(rfiMetrics, OPEN_VQQ_FIELDS, missingFields) +
    sumFields(assessmentMetrics, OPEN_ASSESSMENT_FIELDS, missingFields);

  return {
    vqqRequests,
    assessmentRequests,
    totalRequests: vqqRequests + assessmentRequests,
    activeRequests,
    missingFields,
  };
}

/**
 * Options for useAccountManagementStats (vendor only).
 * @typedef {Object} UseAccountManagementStatsOptions
 * @property {number} [teamSize] - Override team size (e.g. from usersList.length).
 * @property {number} [providerId] - Provider ID.
 * @property {boolean} [isVendorView] - Whether the view is for a vendor.
 * @property {string} [userType] - The type of user.
 */

/**
 * Hook that computes vendor stats for the Account Management page (VendorAccountManagement).
 * Stats: Team Size, Total Requests / In Progress, Completed VQQ Templates
 *
 * @param {UseAccountManagementStatsOptions} [options] - Optional: teamSize
 * @returns {{ stats, statsDetails, isLoading }}
 */
export function useAccountManagementStats(options = {}) {
  const {
    teamSize: teamSizeOverride,
    teamSummary: teamSummaryData,
    userId,
    userType = "vendor",
    isVendorView = true,
    isSponsorView = false,
    enabled = true,
  } = options;

  const rfisState = useSelector((state) => state.rfisColors);
  const rfisListColorsMeta = useMemo(
    () => rfisState?.rfis || [],
    [rfisState?.rfis],
  );

  // Distinct sponsors across both request domains (vendor view only)
  const {
    data: sponsorCountData,
    isLoading: isLoadingSponsorCount,
    isError: isErrorSponsorCount,
  } = useGetSponsorCount(isVendorView ? userId : null);

  // Vendor RFI/Assessment Metrics
  const {
    data: providerRfiMetricsData,
    isLoading: isLoadingProviderRfiMetrics,
    isError: isErrorProviderRfiMetrics,
  } = useProviderRFIMetrics({ enabled: enabled && isVendorView, providerId: userId });
  const {
    data: providerAssessmentMetricsData,
    isLoading: isLoadingProviderAssessmentMetrics,
    isError: isErrorProviderAssessmentMetrics,
  } = useProviderAssessmentMetrics({ enabled: enabled && isVendorView, providerId: userId });
  const {
    data: rfisVendorListData,
    isLoading: rfiVendorIsLoading,
  } = useGetVendorRfis(
    0,
    1000,
    null,
    null,
    null,
    null,
    null,
    null,
    [userId],
    undefined,
    0,
    enabled && isVendorView
  );

  // Sponsor RFI Metrics
  const {
    data: sponsorRfiMetricsData,
    isLoading: sponsorRfiMetricsIsLoading,
    isError: sponsorRfiMetricsIsError,
  } = useSponsorRFIMetrics({ enabled: enabled && isSponsorView, sponsorId: userId });
  const {
    data: sponsorAssessmentMetricsData,
    isLoading: sponsorAssessmentMetricsIsLoading,
    isError: sponsorAssessmentMetricsIsError,
  } = useAssessmentMetrics({ enabled: enabled && isSponsorView, sponsorId: userId });

  // Metrics calculations
  const completedRfisTemplates = useMemo(
    () =>
      Array.from(
        new Set(
          rfisVendorListData?.items
            ?.filter((item) => item.status?.toLowerCase() === "complete")
            ?.map((item) => item.services),
        ),
      ),
    [rfisVendorListData],
  );

  const teamSize = useMemo(() => {
    if (typeof teamSizeOverride === "number") return String(teamSizeOverride);
    return String(DEFAULT_TEAM_SIZE);
  }, [teamSizeOverride]);

  // Both views read one metrics pair through the same formula, so the two
  // screens cannot drift apart.
  const requestStats = useMemo(
    () =>
      computeRequestStats(
        isVendorView ? providerRfiMetricsData : sponsorRfiMetricsData,
        isVendorView
          ? providerAssessmentMetricsData
          : sponsorAssessmentMetricsData,
      ),
    [
      isVendorView,
      providerRfiMetricsData,
      providerAssessmentMetricsData,
      sponsorRfiMetricsData,
      sponsorAssessmentMetricsData,
    ],
  );

  const hasMetricsError = isVendorView
    ? isErrorProviderRfiMetrics || isErrorProviderAssessmentMetrics
    : sponsorRfiMetricsIsError || sponsorAssessmentMetricsIsError;

  const hasCompleteMetrics =
    !hasMetricsError && requestStats.missingFields.length === 0;

  const stats = useMemo(() => {
    const totalRequests = hasCompleteMetrics
      ? requestStats.totalRequests
      : INCOMPLETE_VALUE;
    const activeRequests = hasCompleteMetrics
      ? requestStats.activeRequests
      : INCOMPLETE_VALUE;

    // Return stats based on view type
    return isVendorView ? [
      { name: "Team Size", value: teamSize },
      {
        name: "Total Requests / In Progress",
        value: totalRequests,
        subValue: activeRequests,
        subValueColor: "text-green-600",
        separator: "/",
      },
      {
        name: "Completed VQQ Templates",
        value: completedRfisTemplates.length || 0,
      },
    ] : [
      { name: "Team Size", value: teamSize },
      { name: "Total Requests", value: totalRequests },
      { name: "Active Requests", value: activeRequests },
      // { name: "Platform Credits", value: 35 ?? 0 }, // TODO: get platform credits from API
    ];
  }, [
    teamSize,
    requestStats,
    hasCompleteMetrics,
    completedRfisTemplates.length,
    isVendorView,
  ]);

  // Return stats details based on view type (only vendors for now)
  const statsDetails = useMemo(
    () => isVendorView ? ({
      "Team Size": [
        { label: "Admins", value: teamSummaryData?.admins_count ?? 0 },
        { label: "VQQ Managers - Approvers", value: teamSummaryData?.rfi_managers_approvers_count ?? 0 },
        { label: "VQQ Managers - Users", value: teamSummaryData?.rfi_managers_users_count ?? 0 },
        { label: "VQA Managers", value: teamSummaryData?.vqa_managers_count ?? 0 },
      ],
      "Total Requests / In Progress": [
        {
          label: "Sponsors",
          value:
            !isErrorSponsorCount &&
            typeof sponsorCountData?.distinct_sponsor_count === "number"
              ? sponsorCountData.distinct_sponsor_count
              : INCOMPLETE_VALUE,
        },
        {
          label: "VQQ Requests",
          value: hasCompleteMetrics
            ? requestStats.vqqRequests
            : INCOMPLETE_VALUE,
          link: userType === "vendor" ? "/Provider/RFI/Requests" : "/Backend/Rfis",
        },
        {
          label: "Assessment Requests",
          value: hasCompleteMetrics
            ? requestStats.assessmentRequests
            : INCOMPLETE_VALUE,
          link: userType === "vendor" ? "/Provider/Assessment/History" : "/Backend/Assessment/Request/History",
        },
      ],
      "Completed VQQ Templates": completedRfisTemplates
        .slice(0, 9)
        .map((rfi) => {
          const meta = findRfiColorMeta(rfi, rfisListColorsMeta);
          return {
            label: rfi,
            badge: true,
            badgeColor: meta
              ? [meta.bg, meta.text, meta.border].filter(Boolean).join(" ")
              : "bg-gray-100 text-gray-700",
          };
        }),
    }) : {},
    [
      sponsorCountData,
      isErrorSponsorCount,
      requestStats,
      hasCompleteMetrics,
      completedRfisTemplates,
      rfisListColorsMeta,
      userType,
      isVendorView
    ],
  );

  // Only the queries the active view actually runs.
  const isLoading = isVendorView
    ? isLoadingProviderRfiMetrics ||
      isLoadingProviderAssessmentMetrics ||
      isLoadingSponsorCount ||
      rfiVendorIsLoading
    : sponsorRfiMetricsIsLoading || sponsorAssessmentMetricsIsLoading;

  return { stats, statsDetails, isLoading };
}
